import type { AssistantModuleKey } from './capability-matrix';

export type AssistantRouteFamilyId =
  | 'assistant-home'
  | 'admissions'
  | 'financial-aid'
  | 'settings'
  | 'students-families'
  | 'attendance'
  | 'academics'
  | 'substitutes'
  | 'faculty'
  | 'finance'
  | 'payroll'
  | 'camps'
  | 'donations'
  | 'tip-a-teacher'
  | 'tuition'
  | 'parent-portal'
  | 'unknown';

export type AssistantRouteFamilyDefinition = {
  id: AssistantRouteFamilyId;
  label: string;
  module: AssistantModuleKey;
  routePrefixes: string[];
  semanticRoot: string | null;
  entityLabel: string;
  baseDataSources: string[];
  greeting: string;
  inputPlaceholder: string;
  summary: string;
  operations: string[];
  suggestedRoutes: Array<{
    label: string;
    path: string;
  }>;
};

const normalizePathname = (value: string | null | undefined): string => {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return '/';
  }
  const withLeadingSlash = raw.startsWith('/') ? raw : `/${raw}`;
  const withoutQuery = withLeadingSlash.split('?')[0] ?? withLeadingSlash;
  const collapsed = withoutQuery.replace(/\/{2,}/g, '/').toLowerCase();
  return collapsed.length > 1 && collapsed.endsWith('/') ? collapsed.slice(0, -1) : collapsed;
};

export const ASSISTANT_ROUTE_FAMILIES: AssistantRouteFamilyDefinition[] = [
  {
    id: 'assistant-home',
    label: 'Assistant',
    module: 'general',
    routePrefixes: ['/school/assistant'],
    semanticRoot: null,
    entityLabel: 'assistant work',
    baseDataSources: ['Quick Actions', 'Recent Threads', 'Files'],
    greeting: 'Hi! I can help across admissions, financial aid, reporting, and route-aware workflow follow-up.',
    inputPlaceholder: 'Ask across school operations, reports, artifacts, or the next workflow you want to run...',
    summary: 'Assistant home provides cross-module quick actions, recent thread recall, files, and workspace previews.',
    operations: ['Cross-module quick actions', 'Recent threads', 'Files', 'Workspace previews'],
    suggestedRoutes: [
      { label: 'Assistant Home', path: '/school/assistant' },
    ],
  },
  {
    id: 'admissions',
    label: 'Admissions',
    module: 'admissions',
    routePrefixes: ['/school/admissions'],
    semanticRoot: '/admin/admissions',
    entityLabel: 'applications',
    baseDataSources: ['Applications', 'Timeline', 'Calendar'],
    greeting: 'Hi! I can help review admissions applications, surface missing items, and summarize applicant status.',
    inputPlaceholder: 'Ask about admissions pipeline, interviews, or applicant status...',
    summary: 'Admissions workflows cover pipeline review, applicant detail, deadlines, and follow-up planning.',
    operations: ['Pipeline review', 'Applicant detail', 'Scheduling', 'Documents', 'Follow-up'],
    suggestedRoutes: [
      { label: 'Admissions Dashboard', path: '/school/admissions/dashboard' },
    ],
  },
  {
    id: 'financial-aid',
    label: 'Financial Aid',
    module: 'financial-aid',
    routePrefixes: ['/school/financialaid', '/school/financial-aid'],
    semanticRoot: '/admin/financial-aid',
    entityLabel: 'applications',
    baseDataSources: ['Financial Aid Applications', 'Documents', 'Appeals'],
    greeting: 'Hi! I can help review financial aid applications, documents, and recommendations.',
    inputPlaceholder: 'Ask about aid review, budget risk, or application readiness...',
    summary: 'Financial aid workflows cover queue review, application detail, budget impact, and committee prep.',
    operations: ['Queue review', 'Budget forecasting', 'Flag analysis', 'Committee readiness'],
    suggestedRoutes: [
      { label: 'Financial Aid Dashboard', path: '/school/financialaid/dashboard' },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    module: 'settings',
    routePrefixes: ['/school/settings', '/super-administrator/control-plane'],
    semanticRoot: '/admin/settings',
    entityLabel: 'settings',
    baseDataSources: ['Settings', 'Policies', 'Access'],
    greeting: 'Hi! I can help with settings, staff access, and policy configuration.',
    inputPlaceholder: 'Ask about settings changes, access, or policy updates...',
    summary: 'Settings workflows cover configuration, permissions, admissions setup, and financial-aid setup.',
    operations: ['Configuration', 'User access', 'Admissions settings', 'Financial-aid settings'],
    suggestedRoutes: [
      { label: 'School Settings', path: '/school/settings/general' },
    ],
  },
  {
    id: 'students-families',
    label: 'Students & Families',
    module: 'students-families',
    routePrefixes: ['/school/students'],
    semanticRoot: '/admin/students/staff',
    entityLabel: 'family records',
    baseDataSources: ['Families', 'Students', 'Balance', 'Communications'],
    greeting: 'Hi! I can help with student profiles, family records, communications, and tuition context.',
    inputPlaceholder: 'Ask about family records, student status, balances, or follow-up...',
    summary: 'Students and families workflows cover roster triage, family detail, documents, communications, and balances.',
    operations: ['Roster review', 'Family detail', 'Documents', 'Communications', 'Balance'],
    suggestedRoutes: [
      { label: 'Students Dashboard', path: '/school/students/dashboard' },
    ],
  },
  {
    id: 'attendance',
    label: 'Attendance',
    module: 'attendance',
    routePrefixes: ['/school/attendance', '/super-administrator/control-plane/attendance'],
    semanticRoot: '/admin/attendance',
    entityLabel: 'attendance records',
    baseDataSources: [
      'Attendance Overview',
      'Attendance New Records',
      'Teacher View preview',
      'Student History',
      'Control Plane config/import/audit',
      'Students & Families links',
    ],
    greeting: 'Hi! I can help summarize attendance overview signals, record-entry patterns, and attendance follow-up.',
    inputPlaceholder: 'Ask about attendance trends, record-entry gaps, or follow-up...',
    summary: 'Attendance workflows cover overview review, record entry, teacher preview, student history, control-plane checks, and CSV export.',
    operations: ['Overview summary', 'New Records review', 'Teacher preview', 'Student history', 'Control Plane audit', 'CSV export'],
    suggestedRoutes: [
      { label: 'Attendance Overview', path: '/school/attendance' },
      { label: 'Attendance New Records', path: '/school/attendance/new-records' },
      { label: 'Attendance Teacher View', path: '/school/attendance/teacher-view' },
      { label: 'Attendance Student History', path: '/school/attendance/students/student-1' },
      { label: 'Attendance Control Plane', path: '/super-administrator/control-plane/attendance' },
    ],
  },
  {
    id: 'academics',
    label: 'Academics',
    module: 'general',
    routePrefixes: ['/school/academics'],
    semanticRoot: '/admin/academics',
    entityLabel: 'academic records',
    baseDataSources: ['Gradebook', 'Approvals', 'Report Cards'],
    greeting: 'Hi! I can help summarize academic workflows, grade-entry status, approvals, and report-card preparation.',
    inputPlaceholder: 'Ask about grade entry, approvals, summaries, or report cards...',
    summary: 'Academic workflows cover grade-entry readiness, approvals, summaries, and report-card detail.',
    operations: ['Grade entry', 'Approvals', 'Academic summary', 'Report cards'],
    suggestedRoutes: [
      { label: 'Academics Summary', path: '/school/academics/summary' },
      { label: 'Academic Approvals', path: '/school/academics/approvals' },
    ],
  },
  {
    id: 'substitutes',
    label: 'Substitutes',
    module: 'general',
    routePrefixes: ['/school/substitutes'],
    semanticRoot: '/admin/substitutes',
    entityLabel: 'coverage records',
    baseDataSources: ['Absences', 'Assignments', 'Schedule', 'Payroll'],
    greeting: 'Hi! I can help summarize substitute coverage, open absences, assignments, and payroll follow-up.',
    inputPlaceholder: 'Ask about substitute coverage, assignments, schedule, or payroll...',
    summary: 'Substitute workflows cover absences, coverage assignments, scheduling, and payroll context.',
    operations: ['Absence review', 'Coverage assignments', 'Schedule', 'Payroll'],
    suggestedRoutes: [
      { label: 'Substitute Dashboard', path: '/school/substitutes' },
      { label: 'Substitute Assignments', path: '/school/substitutes/assignments' },
      { label: 'Substitute Payroll', path: '/school/substitutes/payroll' },
    ],
  },
  {
    id: 'faculty',
    label: 'Faculty',
    module: 'faculty',
    routePrefixes: ['/school/faculty', '/bridged/admin/faculty'],
    semanticRoot: '/admin/faculty',
    entityLabel: 'faculty records',
    baseDataSources: ['Faculty Directory', 'Profiles', 'Communications'],
    greeting: 'Hi! I can help summarize faculty profiles, teaching assignments, notes, and communications.',
    inputPlaceholder: 'Ask about faculty coverage, profiles, communications, or notes...',
    summary: 'Faculty workflows cover directory review, profile detail, teaching assignments, notes, and communications.',
    operations: ['Directory review', 'Profile summary', 'Teaching assignments', 'Communications'],
    suggestedRoutes: [
      { label: 'Faculty Directory', path: '/school/faculty' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    module: 'general',
    routePrefixes: ['/school/finance'],
    semanticRoot: '/admin/finance',
    entityLabel: 'financial operations records',
    baseDataSources: ['Ledger', 'Revenue', 'Cash Flow', 'Reconciliation'],
    greeting: 'Hi! I can help summarize financial operations, revenue mix, cash flow, reconciliation, and export workflows.',
    inputPlaceholder: 'Ask about revenue, cash flow, reconciliation, journals, or export...',
    summary: 'Finance workflows cover revenue, cash flow, reconciliation, journals, and export readiness.',
    operations: ['Revenue mix', 'Cash flow', 'Reconciliation', 'Journals', 'Export'],
    suggestedRoutes: [
      { label: 'Finance Overview', path: '/school/finance' },
      { label: 'Finance Reconciliation', path: '/school/finance/reconciliation' },
    ],
  },
  {
    id: 'payroll',
    label: 'Payroll',
    module: 'general',
    routePrefixes: ['/school/payroll'],
    semanticRoot: '/admin/payroll',
    entityLabel: 'payroll records',
    baseDataSources: ['Pay Periods', 'Employee Compensation', 'Audit Log', 'Export History'],
    greeting: 'Hi! I can help summarize pay-period readiness, employee compensation changes, approvals, and export follow-up.',
    inputPlaceholder: 'Ask about payroll readiness, employee detail, approvals, or export...',
    summary: 'Payroll workflows cover pay-period readiness, employee compensation detail, approvals, and export governance.',
    operations: ['Period readiness', 'Employee detail', 'Approvals', 'Export'],
    suggestedRoutes: [
      { label: 'Payroll Dashboard', path: '/school/payroll' },
      { label: 'Payroll Employee Detail', path: '/school/payroll/employee/emp-1' },
    ],
  },
  {
    id: 'camps',
    label: 'Camps',
    module: 'general',
    routePrefixes: ['/school/camps'],
    semanticRoot: '/admin/camps',
    entityLabel: 'camp programs',
    baseDataSources: ['Camp Programs', 'Registrations', 'Waitlist'],
    greeting: 'Hi! I can help summarize camp programs, registration readiness, waitlist activity, and staff follow-up.',
    inputPlaceholder: 'Ask about camps, registrations, capacity, or follow-up...',
    summary: 'Camps workflows cover program setup, registrations, waitlists, and operational readiness.',
    operations: ['Program summary', 'Registration review', 'Capacity', 'Waitlist'],
    suggestedRoutes: [
      { label: 'School Camps', path: '/school/camps' },
      { label: 'Camp Program Detail', path: '/school/camps/camp-1' },
    ],
  },
  {
    id: 'donations',
    label: 'Donations',
    module: 'general',
    routePrefixes: ['/school/donations'],
    semanticRoot: '/admin/donations',
    entityLabel: 'campaign records',
    baseDataSources: ['Campaigns', 'Ledger', 'Moderation', 'Settings'],
    greeting: 'Hi! I can help summarize donation campaigns, ledger activity, moderation needs, and settings follow-up.',
    inputPlaceholder: 'Ask about donation campaigns, ledger activity, moderation, or settings...',
    summary: 'Donations workflows cover campaign performance, ledger activity, moderation queues, and donation settings.',
    operations: ['Campaign review', 'Ledger activity', 'Moderation', 'Settings'],
    suggestedRoutes: [
      { label: 'Donations Dashboard', path: '/school/donations' },
      { label: 'Donations Ledger', path: '/school/donations/ledger' },
    ],
  },
  {
    id: 'tip-a-teacher',
    label: 'Tip a Teacher',
    module: 'general',
    routePrefixes: ['/school/tip-a-teacher'],
    semanticRoot: '/admin/tip-a-teacher',
    entityLabel: 'teacher tip records',
    baseDataSources: ['Teacher Tips', 'Program Settings', 'Notifications'],
    greeting: 'Hi! I can help summarize teacher tip activity, program settings, and notification follow-up.',
    inputPlaceholder: 'Ask about teacher tips, program settings, notifications, or follow-up...',
    summary: 'Tip a Teacher workflows cover program status, teacher totals, settings, and notification readiness.',
    operations: ['Program summary', 'Teacher totals', 'Settings review', 'Notification follow-up'],
    suggestedRoutes: [
      { label: 'Tip a Teacher', path: '/school/tip-a-teacher' },
      { label: 'Tip a Teacher Settings', path: '/school/tip-a-teacher/settings' },
    ],
  },
  {
    id: 'tuition',
    label: 'Tuition',
    module: 'tuition',
    routePrefixes: ['/school/tuition', '/administrator/tuition'],
    semanticRoot: '/admin/tuition',
    entityLabel: 'tuition records',
    baseDataSources: ['Accounts', 'Billing', 'Payments', 'Adjustments'],
    greeting: 'Hi! I can help summarize tuition accounts, billing operations, payment risk, and administrator follow-up.',
    inputPlaceholder: 'Ask about tuition accounts, billing actions, payments, or risk...',
    summary: 'Tuition workflows cover account review, billing operations, payment follow-up, and administrator actions.',
    operations: ['Account review', 'Billing actions', 'Payments', 'Adjustments'],
    suggestedRoutes: [
      { label: 'School Tuition', path: '/school/tuition' },
      { label: 'Administrator Tuition', path: '/administrator/tuition' },
    ],
  },
  {
    id: 'parent-portal',
    label: 'Parent Portal',
    module: 'parent-portal',
    routePrefixes: ['/parent/portal', '/parent/portal--new', '/portal/family'],
    semanticRoot: '/portal/family',
    entityLabel: 'family records',
    baseDataSources: ['Applications', 'Portal Calendar', 'Financial Aid'],
    greeting: 'Hi! I can help summarize applications, deadlines, and next steps for your family.',
    inputPlaceholder: 'Ask about your family applications, deadlines, or next steps...',
    summary: 'Parent portal workflows cover family applications, deadlines, and parent-facing next steps.',
    operations: ['Applications', 'Deadlines', 'Financial aid status'],
    suggestedRoutes: [
      { label: 'Parent Portal Dashboard', path: '/parent/portal/dashboard' },
    ],
  },
  {
    id: 'unknown',
    label: 'Operations',
    module: 'general',
    routePrefixes: [],
    semanticRoot: null,
    entityLabel: 'records',
    baseDataSources: ['Records', 'Activity', 'Documents'],
    greeting: 'Hi! I can help across school operations, summarize the current route, and route work into the right workflows.',
    inputPlaceholder: 'Ask about the current route, school operations, or a task you want completed...',
    summary: 'General operations routing covers broad staff questions, route interpretation, and cross-module workflow routing.',
    operations: ['Route summary', 'Workflow routing', 'Cross-module guidance'],
    suggestedRoutes: [
      { label: 'Admissions Dashboard', path: '/school/admissions/dashboard' },
      { label: 'School Settings', path: '/school/settings/general' },
    ],
  },
];

export const routeMatchesPrefix = (pathname: string, prefix: string): boolean => {
  const normalizedPath = normalizePathname(pathname);
  const normalizedPrefix = normalizePathname(prefix);
  return normalizedPath === normalizedPrefix || normalizedPath.startsWith(`${normalizedPrefix}/`);
};

export const resolveAssistantRouteFamily = (
  pathname: string | null | undefined
): AssistantRouteFamilyDefinition => {
  const normalizedPath = normalizePathname(pathname);
  let matched: AssistantRouteFamilyDefinition | null = null;
  let matchedPrefixLength = -1;

  for (const family of ASSISTANT_ROUTE_FAMILIES) {
    for (const prefix of family.routePrefixes) {
      if (!routeMatchesPrefix(normalizedPath, prefix)) {
        continue;
      }
      if (!matched || prefix.length > matchedPrefixLength) {
        matched = family;
        matchedPrefixLength = prefix.length;
      }
    }
  }

  return matched ?? ASSISTANT_ROUTE_FAMILIES.find((entry) => entry.id === 'unknown')!;
};

export const isAssistantParentRoutePath = (pathname: string | null | undefined): boolean => (
  resolveAssistantRouteFamily(pathname).id === 'parent-portal'
);

export const isAssistantSchoolStaffRoutePath = (pathname: string | null | undefined): boolean => {
  const normalized = normalizePathname(pathname);
  return normalized.startsWith('/school/')
    || normalized.startsWith('/administrator/')
    || normalized.startsWith('/super-administrator/');
};
